import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Exam Timer Hook
 * Manages countdown timer for exams with auto-submit on expiry
 */
export const useExamTimer = (durationMinutes, startTime, onTimeUp = null) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const intervalRef = useRef(null);
  const onTimeUpRef = useRef(onTimeUp);

  // Update callback ref
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  // Calculate initial time remaining
  useEffect(() => {
    if (durationMinutes && startTime) {
      const startDate = new Date(startTime);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
      const now = new Date();
      const remaining = Math.max(0, Math.floor((endDate - now) / 1000));
      setTimeRemaining(remaining);
      setIsRunning(remaining > 0);
    }
  }, [durationMinutes, startTime]);

  // Timer countdown
  useEffect(() => {
    if (isRunning && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsTimeUp(true);
            if (onTimeUpRef.current) {
              onTimeUpRef.current();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isRunning]);

  // Format time as MM:SS or HH:MM:SS
  const formatTime = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Check if time is in warning zone (less than 5 minutes)
  const isWarning = timeRemaining > 0 && timeRemaining <= 300;

  // Check if time is critical (less than 1 minute)
  const isCritical = timeRemaining > 0 && timeRemaining <= 60;

  // Pause timer (not used in exams but available)
  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  // Resume timer
  const resume = useCallback(() => {
    if (timeRemaining > 0) {
      setIsRunning(true);
    }
  }, [timeRemaining]);

  // Stop timer
  const stop = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  // Get percentage of time remaining
  const percentageRemaining = durationMinutes
    ? (timeRemaining / (durationMinutes * 60)) * 100
    : 0;

  return {
    timeRemaining,
    formattedTime: formatTime(timeRemaining),
    isRunning,
    isTimeUp,
    isWarning,
    isCritical,
    percentageRemaining,
    pause,
    resume,
    stop,
  };
};

export default useExamTimer;
